import { Inject, Injectable, InjectionToken, PLATFORM_ID } from "@angular/core";
import { Observable, lastValueFrom, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";
import { Meta } from "@angular/platform-browser";
import { isPlatformBrowser } from "@angular/common";
import { AddonService } from "./addon.service";

export interface AppAppearanceData {
  theme: ColorTheme;
}

export const enum ColorTheme {
  DefaultGreen = 1,
  PersianGreen,
  PinkishOrange,
  Lipstick,
  YellowishOrange,
  Azure,
  LightishBlue,
  DodgerBlue,
  BlueBayoux,
  Black,
  Golden,
  LightGreen
}

export const THEMES_COLORS_MAP = {
  [ColorTheme.DefaultGreen]: { colors: ["#6cb33f", "#a0da6c"], orderNumber: 2, index: 0 },
  [ColorTheme.PersianGreen]: { colors: ["#008657", "#008657"], orderNumber: 1, index: 1 },
  [ColorTheme.PinkishOrange]: { colors: ["#f26648", "#f26648"], orderNumber: 9, index: 2 },
  [ColorTheme.Lipstick]: { colors: ["#111E6C", "#111E6C"], orderNumber: 4, index: 3 },
  [ColorTheme.YellowishOrange]: { colors: ["#f8971d", "#f8971d"], orderNumber: 10, index: 4 },
  [ColorTheme.Azure]: { colors: ["#00aeef", "#00aeef"], orderNumber: 6, index: 5 },
  [ColorTheme.LightishBlue]: { colors: ["#3A7ABE", "#3A7ABE"], orderNumber: 5, index: 6 },
  [ColorTheme.DodgerBlue]: { colors: ["#6666cc", "#6666cc"], orderNumber: 7, index: 7 },
  [ColorTheme.BlueBayoux]: { colors: ["#525f81", "#525f81"], orderNumber: 8, index: 8 },
  [ColorTheme.Black]: { colors: ["#303030", "#303030"], orderNumber: 12, index: 9 },
  [ColorTheme.Golden]: { colors: ["#b68a35", "rgba(182, 138, 53, 0.6)"], orderNumber: 11, index: 10 },
  [ColorTheme.LightGreen]: { colors: ["#91C740", "#91C740"], orderNumber: 3, index: 11 },
};

export const APP_APPEARANCE_ADDON_NAME = "App Appearance";

@Injectable({
  providedIn: "root"
})
export class AppAppearanceService {
  private cachedAppAppearance: { [groupId: string]: AppAppearanceData } = {};

  constructor(private http: HttpClient,
    private addonService: AddonService,
    private meta: Meta,    
    @Inject(PLATFORM_ID) public platformId: InjectionToken<Object>,
  ) { }

  getAppAppearanceData(groupId: string): Observable<AppAppearanceData | null> {
    if (this.cachedAppAppearance[groupId]) {
      return of(this.cachedAppAppearance[groupId]);
    }
    return this.addonService.getAddons(groupId).pipe(
      map((addons = []) => {
        return this.getAppearanceData(addons);
      })
    );
  }

  /**
   * Method will set global variables for theme color that will be used by Angular Material Theme
   * @param theme 
   * @returns void
   */
  setColorTheme(theme: ColorTheme): void {
    const themeColors = THEMES_COLORS_MAP[theme];
    this.meta.updateTag({
      name: 'theme-color',
      content: themeColors.colors[0]
    })
    if (isPlatformBrowser(this.platformId)) {

      document.documentElement.style.setProperty(
        "--primary-color",
        themeColors.colors[0]
      );
      document.documentElement.style.setProperty(
        "--primary-color-rgb",
        this.hexToRgb(themeColors.colors[0])
      );
      document.documentElement.style.setProperty(
        "--primary-color-lighter",
        themeColors.colors[1]
      );
    }


  }
  
  private hexToRgb(hex: string) {
    const result: any = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
  }

  /**
   * Method to set default theme color by getting addons and setting theme color on basis of App Appearance addon and return AppAppearanceData
   * If App Appearance addon is not found then it will set DefaultGreen theme
   * Use this method is you don't have addons available and you have groupId only 
   * @param groupId
   * @returns Observable of AppAppearanceData or null
   */
  public setDefaultThemeColor(groupId: string): Observable<AppAppearanceData | null> {
    return this.getAppAppearanceData(groupId).pipe(
      map((appAppearance) => {
        if (!appAppearance) {
          this.setColorTheme(ColorTheme.DefaultGreen);
        } else {
          const theme = appAppearance?.theme || ColorTheme.DefaultGreen;
          this.setColorTheme(theme);
        }
        return appAppearance;
      })
    );
  }

  /**
   * Use this method to pass complete list of addons to set theme color
   * Theme color will be set based on App Appearance addon
   * If App Appearance addon is not found then it will set DefaultGreen theme
   * @param addons List of addons
   * @returns void 
   */
  public setDefaultThemeColorAddons(addons: any): void {
    const appAppearance = this.getAppearanceData(addons);
    if (!appAppearance) {
      this.setColorTheme(ColorTheme.DefaultGreen);
    } else {
      const theme = appAppearance?.theme || ColorTheme.DefaultGreen;
      this.setColorTheme(theme);
    }
  }

  /**
   * Use this method to pass complete list of addons to get AppAppearanceData which is theme used to set theme color
   * This method will find App Appearance addon from the list and return AppAppearanceData
   * If App Appearance addon is not found then it will return null
   * If organizationId is not found in App Appearance addon or does not have associated theme then it will return DefaultGreen theme
   * @param addons List of addons
   * @returns AppAppearanceData or null
   */
  getAppearanceData(addons: any[]): AppAppearanceData | null {
    const appearanceAddon = addons.find(
      a => a.Type === APP_APPEARANCE_ADDON_NAME
    );
    if (!appearanceAddon) {
      return null;
    }
    const orgId = Number(appearanceAddon.PublicProperties?.organziationId);
    if (
      orgId >
      Object.keys(THEMES_COLORS_MAP).length
    ) {
      return {
        theme: ColorTheme.DefaultGreen
      };
    }
    return {
      theme:
        orgId ||
        ColorTheme.DefaultGreen
    };
  }

  getDefaultTheme(){
    return ColorTheme.DefaultGreen
  }


  getThemeColors(theme: number){
    return THEMES_COLORS_MAP[theme].colors[0];
  }


}
